import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from 'jwt-decode';

// Application configuration
const APPLICATIONS = {
  PART1: {
    id: "PART1",
    name: "MISSING: The Search for Terry Hughes",
    shortName: "MISSING Part I"
  },
  PART2: {
    id: "PART2",
    name: "MISSING: The Final Secret",
    shortName: "MISSING Part II"
  }
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("login"); // login, forgotPassword, confirmCode
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch("https://admin.cretecinc.com/authenticate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error("Login failed");
      }

      const data = await response.json();
      const accessToken = data.AccessToken;

      // Store token in localStorage
      localStorage.setItem("token", accessToken);

      // Decode the token
      const decoded = jwtDecode(accessToken);
      const groups = decoded["cognito:groups"] || [];

      // Redirect based on group
      if (groups.includes("Admin")) {
        navigate("/admin");
      } else if (groups.includes("Buyers")) {
        navigate("/buyer");
      } else if (groups.includes("Users")) {
        navigate("/user");
      } else if (groups.includes("Superuser")) {
        navigate("/superuser");
      } else {
        setError("User group not recognized.");
      }
    } catch (err) {
      setError("Invalid email or password.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);

    if (!email) {
      setError("Please enter your email address.");
      setLoading(false);
      return;
    }

    try {
      // Call Cognito's forgot password endpoint
      const response = await fetch("https://admin.cretecinc.com/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to initiate password reset");
      }

      setSuccessMessage("A password reset code has been sent to your email address.");
      setView("confirmCode");
    } catch (err) {
      setError(err.message || "Failed to initiate password reset. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);

    if (!email || !resetCode || !newPassword) {
      setError("All fields are required.");
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      setLoading(false);
      return;
    }

    try {
      // Call Cognito's confirm forgot password endpoint
      const response = await fetch("https://admin.cretecinc.com/confirm-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email, 
          confirmationCode: resetCode, 
          newPassword 
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to reset password");
      }

      setSuccessMessage("Your password has been reset successfully. You can now log in with your new password.");
      setNewPassword("");
      setResetCode("");
      setView("login");
    } catch (err) {
      setError(err.message || "Failed to reset password. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const renderLoginForm = () => (
    <form onSubmit={handleLogin}>
      <div style={{ marginBottom: "1.5rem" }}>
        <label style={{ 
          display: "block", 
          marginBottom: "0.5rem", 
          fontWeight: "500", 
          color: "#555"
        }}>
          Email Address
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={{ 
            width: "100%", 
            padding: "0.75rem",
            borderRadius: "4px",
            border: "1px solid #ddd",
            fontSize: "1rem"
          }}
          placeholder="Enter your email"
        />
      </div>
      
      <div style={{ marginBottom: "1.5rem" }}>
        <label style={{ 
          display: "block", 
          marginBottom: "0.5rem", 
          fontWeight: "500", 
          color: "#555"
        }}>
          Password
        </label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          style={{ 
            width: "100%", 
            padding: "0.75rem",
            borderRadius: "4px",
            border: "1px solid #ddd",
            fontSize: "1rem"
          }}
          placeholder="Enter your password"
        />
      </div>
      
      <button 
        type="submit" 
        disabled={loading}
        style={{ 
          width: "100%", 
          padding: "0.75rem", 
          backgroundColor: "#4CAF50", 
          color: "white", 
          border: "none",
          borderRadius: "4px",
          fontSize: "1rem",
          fontWeight: "500",
          cursor: loading ? "default" : "pointer",
          opacity: loading ? 0.7 : 1
        }}
      >
        {loading ? "Signing in..." : "Sign In"}
      </button>
      
      <p style={{ 
        marginTop: "1.5rem", 
        textAlign: "center",
        fontSize: "0.9rem",
        color: "#666"
      }}>
        <a 
          href="#" 
          onClick={(e) => {
            e.preventDefault();
            setView("forgotPassword");
            setError("");
            setSuccessMessage("");
          }}
          style={{ 
            color: "#2196F3", 
            textDecoration: "none" 
          }}
        >
          Forgot password?
        </a>
      </p>
    </form>
  );

  const renderForgotPasswordForm = () => (
    <form onSubmit={handleForgotPassword}>
      <div style={{ marginBottom: "1.5rem" }}>
        <label style={{ 
          display: "block", 
          marginBottom: "0.5rem", 
          fontWeight: "500", 
          color: "#555"
        }}>
          Email Address
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={{ 
            width: "100%", 
            padding: "0.75rem",
            borderRadius: "4px",
            border: "1px solid #ddd",
            fontSize: "1rem"
          }}
          placeholder="Enter your email"
        />
      </div>
      
      <button 
        type="submit" 
        disabled={loading}
        style={{ 
          width: "100%", 
          padding: "0.75rem", 
          backgroundColor: "#2196F3", 
          color: "white", 
          border: "none",
          borderRadius: "4px",
          fontSize: "1rem",
          fontWeight: "500",
          cursor: loading ? "default" : "pointer",
          opacity: loading ? 0.7 : 1
        }}
      >
        {loading ? "Sending..." : "Send Reset Code"}
      </button>
      
      <p style={{ 
        marginTop: "1.5rem", 
        textAlign: "center",
        fontSize: "0.9rem",
        color: "#666"
      }}>
        <a 
          href="#" 
          onClick={(e) => {
            e.preventDefault();
            setView("login");
            setError("");
            setSuccessMessage("");
          }}
          style={{ 
            color: "#2196F3", 
            textDecoration: "none" 
          }}
        >
          Back to Login
        </a>
      </p>
    </form>
  );

  const renderConfirmCodeForm = () => (
    <form onSubmit={handleResetPassword}>
      <div style={{ marginBottom: "1.5rem" }}>
        <label style={{ 
          display: "block", 
          marginBottom: "0.5rem", 
          fontWeight: "500", 
          color: "#555"
        }}>
          Email Address
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={{ 
            width: "100%", 
            padding: "0.75rem",
            borderRadius: "4px",
            border: "1px solid #ddd",
            fontSize: "1rem"
          }}
          placeholder="Enter your email"
        />
      </div>
      
      <div style={{ marginBottom: "1.5rem" }}>
        <label style={{ 
          display: "block", 
          marginBottom: "0.5rem", 
          fontWeight: "500", 
          color: "#555"
        }}>
          Reset Code
        </label>
        <input
          type="text"
          value={resetCode}
          onChange={(e) => setResetCode(e.target.value)}
          required
          style={{ 
            width: "100%", 
            padding: "0.75rem",
            borderRadius: "4px",
            border: "1px solid #ddd",
            fontSize: "1rem"
          }}
          placeholder="Enter the reset code from your email"
        />
      </div>
      
      <div style={{ marginBottom: "1.5rem" }}>
        <label style={{ 
          display: "block", 
          marginBottom: "0.5rem", 
          fontWeight: "500", 
          color: "#555"
        }}>
          New Password
        </label>
        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          style={{ 
            width: "100%", 
            padding: "0.75rem",
            borderRadius: "4px",
            border: "1px solid #ddd",
            fontSize: "1rem"
          }}
          placeholder="Enter your new password"
        />
      </div>
      
      <button 
        type="submit" 
        disabled={loading}
        style={{ 
          width: "100%", 
          padding: "0.75rem", 
          backgroundColor: "#4CAF50", 
          color: "white", 
          border: "none",
          borderRadius: "4px",
          fontSize: "1rem",
          fontWeight: "500",
          cursor: loading ? "default" : "pointer",
          opacity: loading ? 0.7 : 1
        }}
      >
        {loading ? "Resetting..." : "Reset Password"}
      </button>
      
      <p style={{ 
        marginTop: "1.5rem", 
        textAlign: "center",
        fontSize: "0.9rem",
        color: "#666"
      }}>
        <a 
          href="#" 
          onClick={(e) => {
            e.preventDefault();
            setView("login");
            setError("");
            setSuccessMessage("");
          }}
          style={{ 
            color: "#2196F3", 
            textDecoration: "none" 
          }}
        >
          Back to Login
        </a>
      </p>
    </form>
  );

  return (
    <div style={{ 
      maxWidth: "500px", 
      margin: "0 auto", 
      padding: "2rem",
      borderRadius: "8px",
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
      backgroundColor: "white",
      marginTop: "3rem"
    }}>
      <h1 style={{ 
        fontSize: "1.5rem", 
        textAlign: "center", 
        marginBottom: "1rem",
        color: "#333"
      }}>
        Welcome to MISSING
      </h1>
      <h2 style={{ 
        fontSize: "1rem", 
        textAlign: "center", 
        marginBottom: "0.5rem",
        fontWeight: "normal",
        color: "#666"
      }}>
        {APPLICATIONS.PART1.name}
      </h2>
      <h2 style={{ 
        fontSize: "1rem", 
        textAlign: "center", 
        marginBottom: "2rem",
        fontWeight: "normal",
        color: "#666"
      }}>
        {APPLICATIONS.PART2.name}
      </h2>
      <p style={{ 
        fontSize: "0.9rem", 
        textAlign: "center", 
        marginBottom: "2rem",
        fontWeight: "normal",
        color: "#666",
        fontStyle: "italic"
      }}>
        Applications to Train the Recognition and Mitigation of Cognitive Biases
      </p>
      
      {error && (
        <div style={{ 
          color: "#d32f2f", 
          backgroundColor: "#ffebee", 
          padding: "0.75rem", 
          borderRadius: "4px",
          marginBottom: "1.5rem"
        }}>
          {error}
        </div>
      )}
      
      {successMessage && (
        <div style={{ 
          color: "#2e7d32", 
          backgroundColor: "#e8f5e9", 
          padding: "0.75rem", 
          borderRadius: "4px",
          marginBottom: "1.5rem"
        }}>
          {successMessage}
        </div>
      )}
      
      {view === "login" && renderLoginForm()}
      {view === "forgotPassword" && renderForgotPasswordForm()}
      {view === "confirmCode" && renderConfirmCodeForm()}
      
      {view === "login" && (
        <p style={{ 
          marginTop: "1rem", 
          fontSize: "0.9rem", 
          color: "#666",
          textAlign: "center" 
        }}>
          New users must receive an invitation email before they can log in.
        </p>
      )}
        <div style={{ marginTop: "2rem", textAlign: "center", fontSize: "0.9rem" }}>
          Need help?{" "}
          <a 
            href="mailto:support@creativetechnologiesincla.zohodesk.com" 
            style={{ color: "#2196F3", textDecoration: "none" }}
          >
            Contact Support
          </a>
        </div>    
    </div>
  );
};

export default Login;